import {
	Accordion,
	Icon,
	Input,
	type JVSIcons,
	Menu,
	Message,
	Popup,
} from '@mairistem/composants';
import { deburr } from 'lodash';
import React from 'react';
import './BlockList.css';

interface BlockItem {
	codeBlock: string;
	label: string;
}

interface TypeBlockItemsProps {
	type: string;
	icon: JVSIcons;
	blocks: BlockItem[];
}

interface TypeBlockListProps {
	blocks: TypeBlockItemsProps[];
	onSelect: (block: BlockItem) => void;
	popupTrigger: React.ReactNode;
}

const normaliser = (valeur: string) => deburr(valeur.toLowerCase());

const BlockList: React.FC<TypeBlockListProps> = ({
	blocks,
	onSelect,
	popupTrigger,
}: TypeBlockListProps) => {
	const [recherche, setRecherche] = React.useState('');

	const typesTries = React.useMemo(
		() =>
			[...(blocks ?? [])]
				.sort((a, b) => a.type.localeCompare(b.type))
				.map((typeBlock) => ({
					...typeBlock,
					blocks: [...typeBlock.blocks]
						.sort((a, b) => a.label.localeCompare(b.label))
						.map((block) => ({ ...block, icon: typeBlock.icon })),
				})),
		[blocks],
	);

	if (!blocks || blocks.length === 0) {
		return null;
	}

	const handleRechercheChange = (_e, { value }: { value: string }) => {
		setRecherche(normaliser(value.trim()));
	};

	const resultats = typesTries.flatMap((typeBlock) =>
		typeBlock.blocks.filter((block) =>
			normaliser(block.label).includes(recherche),
		),
	);

	const renderBlocks = (items: (BlockItem & { icon: JVSIcons })[]) => (
		<Menu vertical fluid secondary className="block-list-menu">
			{items.map((block) => (
				<Menu.Item
					key={block.codeBlock}
					data-testid="chooseBlock"
					onClick={() => onSelect(block)}
				>
					<Icon iconSet="Lucide" name={block.icon} size={16} />
					{block.label}
				</Menu.Item>
			))}
		</Menu>
	);

	const panels = typesTries.map((typeBlock) => ({
		key: typeBlock.type,
		title: {
			content: typeBlock.type,
			'data-testid': 'openAccordionBloks',
		},
		content: { content: renderBlocks(typeBlock.blocks) },
	}));

	return (
		<Popup
			basic
			trigger={popupTrigger}
			position="bottom right"
			className="block-list-popup"
			on="click"
			positionFixed
		>
			<Popup.Header data-testid="blockListHeader">
				<Input
					fluid
					icon="search"
					iconPosition="left"
					placeholder="Rechercher un bloc"
					data-testid="blockListSearch"
					input={{ 'data-testid': 'blockListSearchInput' }}
					onChange={handleRechercheChange}
				/>
			</Popup.Header>
			<Popup.Content data-testid="blockListContent" className="block-list">
				{recherche &&
					(resultats.length > 0 ? (
						renderBlocks(resultats)
					) : (
						<Message size="tiny" content="Aucun résultat trouvé" />
					))}
				{!recherche && <Accordion panels={panels} fluid exclusive={false} />}
			</Popup.Content>
		</Popup>
	);
};

export default BlockList;
